import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Checkbox } from '../components/controls'
import { Flag } from '../components/flag'

export interface Language {
  languageId: number
  code: string
  localCode: string
  name: string
  nativeName: string
  lcid: number
  msgLangId: number
  isPlatformLanguage: boolean
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface LanguagePage {
  languages: Language[]
  total: number
  offset: number
  limit: number
}

interface FormValue {
  code: string
  localCode: string
  name: string
  nativeName: string
  lcid: string
  msgLangId: string
  isPlatformLanguage: boolean
}

const emptyForm: FormValue = {
  code: '',
  localCode: '',
  name: '',
  nativeName: '',
  lcid: '',
  msgLangId: '',
  isPlatformLanguage: false,
}

const PAGE_SIZE = 50

/**
 * LanguagesPage is the language catalogue — [Catalog].[Platform.Language]: what
 * the translation keys hang off.
 *
 * Name and native name are two different things and both are shown: Name is the
 * English label (two rows are called "English" and two "Portuguese") while
 * NativeName is what the speaker calls it — "Italiano", "日本語".
 *
 * LCID and MsgLangID are kept apart on purpose. The first is the locale, the
 * second is the locale its MESSAGES fall back to, and four production rows
 * differ: Brazilian Portuguese takes English messages.
 */
export function LanguagesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Language | null>(null)
  const [form, setForm] = useState<FormValue>(emptyForm)
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['languages', params],
    queryFn: async (): Promise<LanguagePage> => {
      const res = await api.get('/languages', { params })
      return res.data as LanguagePage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['languages'] })
  const set = (patch: Partial<FormValue>) => setForm((f) => ({ ...f, ...patch }))

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (l: Language) => {
    setEditing(l)
    setForm({
      code: l.code,
      localCode: l.localCode,
      name: l.name,
      nativeName: l.nativeName,
      lcid: String(l.lcid),
      msgLangId: String(l.msgLangId),
      isPlatformLanguage: l.isPlatformLanguage,
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code.trim(),
        localCode: form.localCode.trim(),
        name: form.name.trim(),
        nativeName: form.nativeName.trim(),
        lcid: Number(form.lcid),
        msgLangId: Number(form.msgLangId),
        isPlatformLanguage: form.isPlatformLanguage,
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing ? api.put(`/languages/${editing.languageId}`, body) : api.post('/languages', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('languages.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (l: Language) =>
      l.isDisabledByPlatform
        ? api.post(`/languages/${l.languageId}/restore`)
        : api.delete(`/languages/${l.languageId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('languages.errors.save'))),
  })

  const languages = data?.languages ?? []
  const total = data?.total ?? 0
  const canSubmit =
    form.code.trim() !== '' &&
    form.localCode.trim() !== '' &&
    form.name.trim() !== '' &&
    form.nativeName.trim() !== '' &&
    Number(form.lcid) > 0 &&
    Number(form.msgLangId) > 0

  return (
    <div>
      <PageHeading
        title={t('languages.title')}
        subtitle={t('languages.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('languages.newLanguage')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('languages.editLanguage') : t('languages.newLanguage')}
                  </DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canSubmit) save.mutate()
                  }}
                >
                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-code">{t('languages.columns.code')}</Label>
                      <Input
                        id="language-code"
                        value={form.code}
                        onChange={(e) => set({ code: e.target.value })}
                        maxLength={10}
                        className="font-mono"
                        placeholder="it"
                        autoFocus
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-local">{t('languages.columns.localCode')}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="language-local"
                          value={form.localCode}
                          onChange={(e) => set({ localCode: e.target.value })}
                          maxLength={10}
                          className="font-mono"
                          placeholder="it-IT"
                        />
                        {/* The flag follows what you type: it is derived from
                            the region, so it doubles as a check on the code. */}
                        <Flag locale={form.localCode} className="w-6 text-center" />
                      </div>
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('languages.codeHint')}</p>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-name">{t('languages.columns.name')}</Label>
                      <Input
                        id="language-name"
                        value={form.name}
                        onChange={(e) => set({ name: e.target.value })}
                        maxLength={290}
                        placeholder="Italian"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-native">{t('languages.columns.nativeName')}</Label>
                      <Input
                        id="language-native"
                        value={form.nativeName}
                        onChange={(e) => set({ nativeName: e.target.value })}
                        maxLength={290}
                        placeholder="Italiano"
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('languages.nameHint')}</p>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-lcid">{t('languages.columns.lcid')}</Label>
                      <Input
                        id="language-lcid"
                        value={form.lcid}
                        onChange={(e) => set({ lcid: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })}
                        placeholder="1040"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="language-msglang">{t('languages.columns.msgLangId')}</Label>
                      <Input
                        id="language-msglang"
                        value={form.msgLangId}
                        onChange={(e) =>
                          set({ msgLangId: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })
                        }
                        placeholder="1040"
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('languages.lcidHint')}</p>

                  <Checkbox
                    id="language-platform"
                    checked={form.isPlatformLanguage}
                    onCheckedChange={(checked) => set({ isPlatformLanguage: checked })}
                    label={t('languages.columns.platformLanguage')}
                  />
                  <p className="-mt-1 text-xs text-muted-foreground">{t('languages.platformHint')}</p>

                  <ErrorBanner message={error} />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.isPending || !canSubmit}>
                      {editing ? t('common.save') : t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="languages">
        <p>{t('languages.help.line1')}</p>
        <p>{t('languages.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('languages.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('languages.columns.id')}</TableHead>
                  <TableHead className="w-12" aria-label={t('languages.columns.flag')} />
                  <TableHead className="w-24">{t('languages.columns.code')}</TableHead>
                  <TableHead className="w-28">{t('languages.columns.localCode')}</TableHead>
                  <TableHead>{t('languages.columns.name')}</TableHead>
                  <TableHead>{t('languages.columns.nativeName')}</TableHead>
                  <TableHead className="w-32">{t('languages.columns.locale')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {languages.map((l) => (
                  <TableRow key={l.languageId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {l.languageId}
                    </TableCell>
                    <TableCell>
                      <Flag locale={l.localCode} />
                    </TableCell>
                    <TableCell className="font-mono font-medium">{l.code}</TableCell>
                    <TableCell className="font-mono text-xs">{l.localCode}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {l.name}
                        {l.isPlatformLanguage && (
                          <Badge variant="secondary">{t('languages.platform')}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{l.nativeName}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {l.lcid}
                      {l.msgLangId !== l.lcid && (
                        <span title={t('languages.fallbackTitle')}> → {l.msgLangId}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={l.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(l)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={l.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(l)}
                          >
                            {l.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {languages.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 9 : 8}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('languages.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('languages.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
